import { useState } from "react";
import { ArrowUpRight, CheckCircle2, Info, Mail } from "lucide-react";
import { api, formatApiError } from "../api";
import { budgetRanges, contactContent, projectTypes, site } from "../content";
import { Reveal } from "./Reveal";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyForm = {
  name: "",
  email: "",
  company: "",
  project_type: "",
  budget_range: "",
  message: "",
  consent: false,
};

const inputClass =
  "w-full rounded-xl border border-black/15 bg-[#F7F7F5] px-4 py-3 text-sm text-[#1A1A1A] outline-none transition-colors duration-200 placeholder:text-black/35 focus:border-[#1D4E89] focus:bg-white";

const labelClass = "mb-2 block font-mono text-[10px] uppercase tracking-[0.18em] text-black/50";

const Field = ({ id, label, error, children }) => (
  <div>
    <label htmlFor={id} className={labelClass}>
      {label}
    </label>
    {children}
    {error && (
      <p className="mt-1.5 text-xs text-red-600" data-testid={`${id}-error`} role="alert">
        {error}
      </p>
    )}
  </div>
);

export const Contact = () => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [phase, setPhase] = useState(() => (localStorage.getItem("lr_last_lead") ? "done" : "form"));
  const [apiError, setApiError] = useState("");
  const [result, setResult] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("lr_last_lead"));
    } catch {
      return null;
    }
  });

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Informe seu nome.";
    if (!EMAIL_RE.test(form.email.trim())) e.email = "Informe um e-mail válido.";
    if (form.company.trim().length < 2) e.company = "Informe a empresa ou o projeto.";
    if (!form.project_type) e.project_type = "Selecione o tipo de projeto.";
    if (!form.budget_range) e.budget_range = "Selecione a faixa de investimento.";
    if (form.message.trim().length < 10) e.message = "Conte um pouco mais sobre a ideia (mínimo 10 caracteres).";
    if (!form.consent) e.consent = "É necessário autorizar o contato.";
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (phase === "sending") return;
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setApiError("");
    setPhase("sending");
    try {
      const { data } = await api.post("/leads", { ...form, email: form.email.trim() });
      setResult(data);
      localStorage.setItem("lr_last_lead", JSON.stringify(data));
      setPhase("done");
    } catch (err) {
      setApiError(formatApiError(err.response?.data?.detail, "Não foi possível enviar agora. Tente novamente em instantes."));
      setPhase("form");
    }
  };

  const reset = () => {
    localStorage.removeItem("lr_last_lead");
    setForm(emptyForm);
    setErrors({});
    setResult(null);
    setApiError("");
    setPhase("form");
  };

  return (
    <section id="contato" className="scroll-mt-20 bg-[#F7F7F5] py-24 md:py-36" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <Reveal>
              <span className="rounded-full bg-black px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-white">
                {contactContent.label}
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-6 font-display text-4xl font-extrabold leading-[0.98] tracking-tighter text-[#1A1A1A] sm:text-5xl lg:text-6xl">
                {contactContent.headline}
              </h2>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="mt-6 max-w-md text-sm leading-relaxed text-black/60 md:text-base">{site.positioning}</p>
            </Reveal>
            <Reveal delay={0.2}>
              <a
                href={`mailto:${site.email}`}
                data-testid="contact-email-link"
                className="mt-8 inline-flex items-center gap-3 font-mono text-sm text-[#1D4E89] hover:underline"
              >
                <Mail size={15} />
                {site.email}
              </a>
            </Reveal>
            <Reveal delay={0.24}>
              <p
                data-testid="contact-demo-notice"
                className="mt-8 inline-flex items-center gap-2 rounded-full border border-[#1D4E89]/25 bg-[#1D4E89]/5 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.14em] text-[#1D4E89]"
              >
                <Info size={13} />
                {contactContent.demoNotice}
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.12} className="md:col-span-7">
            <div className="rounded-[2rem] border border-black/10 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] md:p-10">
              {phase === "done" && result ? (
                <div className="flex flex-col items-start" data-testid="contact-success">
                  <CheckCircle2 size={42} className="text-[#1D4E89]" strokeWidth={1.5} />
                  <h3 className="mt-6 font-display text-3xl font-extrabold tracking-tighter text-[#1A1A1A] md:text-4xl">
                    Pedido recebido.
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-black/60 md:text-base">
                    Obrigado, {result.name.split(" ")[0]}. Seu pedido foi registrado com a referência:
                  </p>
                  <p
                    data-testid="contact-reference-number"
                    className="mt-5 rounded-xl border border-[#1D4E89]/20 bg-[#1D4E89]/5 px-5 py-3 font-mono text-2xl tracking-[0.1em] text-[#1D4E89] md:text-3xl"
                  >
                    {result.reference_number}
                  </p>
                  <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.14em] text-black/45">
                    Registro de demonstração — apagado automaticamente em 24 horas.
                  </p>
                  <button
                    type="button"
                    data-testid="contact-send-another"
                    onClick={reset}
                    className="mt-8 rounded-full border border-black/15 px-6 py-3 font-display text-xs font-bold uppercase tracking-wide text-black transition-colors duration-300 hover:border-[#1D4E89] hover:text-[#1D4E89]"
                  >
                    Enviar outro pedido
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} noValidate data-testid="contact-form" className="space-y-5">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field id="contact-name" label="Nome" error={errors.name}>
                      <input
                        id="contact-name"
                        data-testid="contact-input-name"
                        type="text"
                        autoComplete="name"
                        placeholder="Seu nome"
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                        className={inputClass}
                      />
                    </Field>
                    <Field id="contact-email" label="E-mail" error={errors.email}>
                      <input
                        id="contact-email"
                        data-testid="contact-input-email"
                        type="email"
                        autoComplete="email"
                        placeholder="voce@empresa.com"
                        value={form.email}
                        onChange={(e) => set("email", e.target.value)}
                        className={inputClass}
                      />
                    </Field>
                  </div>

                  <Field id="contact-company" label="Empresa ou projeto" error={errors.company}>
                    <input
                      id="contact-company"
                      data-testid="contact-input-company"
                      type="text"
                      autoComplete="organization"
                      placeholder="Nome da empresa ou do projeto"
                      value={form.company}
                      onChange={(e) => set("company", e.target.value)}
                      className={inputClass}
                    />
                  </Field>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field id="contact-type" label="Tipo de projeto" error={errors.project_type}>
                      <select
                        id="contact-type"
                        data-testid="contact-select-type"
                        value={form.project_type}
                        onChange={(e) => set("project_type", e.target.value)}
                        className={`${inputClass} appearance-none`}
                      >
                        <option value="" disabled>
                          Selecione
                        </option>
                        {projectTypes.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field id="contact-budget" label="Faixa de investimento" error={errors.budget_range}>
                      <select
                        id="contact-budget"
                        data-testid="contact-select-budget"
                        value={form.budget_range}
                        onChange={(e) => set("budget_range", e.target.value)}
                        className={`${inputClass} appearance-none`}
                      >
                        <option value="" disabled>
                          Selecione
                        </option>
                        {budgetRanges.map((b) => (
                          <option key={b} value={b}>
                            {b}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>

                  <Field id="contact-message" label="Mensagem" error={errors.message}>
                    <textarea
                      id="contact-message"
                      data-testid="contact-input-message"
                      rows={4}
                      placeholder="Conte sobre a ideia, o produto ou o lançamento."
                      value={form.message}
                      onChange={(e) => set("message", e.target.value)}
                      className={`${inputClass} resize-none`}
                    />
                  </Field>

                  <div>
                    <label htmlFor="contact-consent" className="flex cursor-pointer items-start gap-3">
                      <input
                        id="contact-consent"
                        data-testid="contact-input-consent"
                        type="checkbox"
                        checked={form.consent}
                        onChange={(e) => set("consent", e.target.checked)}
                        className="mt-0.5 h-4 w-4 shrink-0 accent-[#1D4E89]"
                      />
                      <span className="text-xs leading-relaxed text-black/60">{contactContent.consentLabel}</span>
                    </label>
                    {errors.consent && (
                      <p className="mt-1.5 text-xs text-red-600" data-testid="contact-consent-error" role="alert">
                        {errors.consent}
                      </p>
                    )}
                  </div>

                  {apiError && (
                    <p
                      data-testid="contact-error"
                      role="alert"
                      className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                    >
                      {apiError}
                    </p>
                  )}

                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={phase === "sending"}
                    className="group flex w-full items-center justify-center gap-3 rounded-full bg-black py-4 font-display text-sm font-bold uppercase tracking-wide text-white transition-colors duration-300 hover:bg-[#1D4E89] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {phase === "sending" ? "Enviando…" : "Enviar pedido de projeto"}
                    {phase !== "sending" && (
                      <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:rotate-45" />
                    )}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
